/**
 * Where the signalling socket lives.
 *
 * `wss://<host>/stream/ws` is NOT under `/api/v1` — it is a separate service
 * (`sv_stream`) that the edge routes on its own, so it cannot be derived by appending
 * to API_BASE_URL. What it CAN be derived from is the origin that base already points
 * at, and that keeps one rule for both builds:
 *
 *   native   API_BASE_URL = https://api.sam-vision.uz/api/v1  →  wss://api.sam-vision.uz/stream/ws
 *   web      API_BASE_URL = /api/v1  (same-origin, proxied)   →  wss://<page origin>/stream/ws
 *
 * ⚠️ The web line depends on the cabinet's vhost proxying `/stream/` the way it
 * already proxies `/api/` — `docs/api/cameras/live-video.md` §5 names this as the open
 * deployment question and the blocker for the browser build. The dev server does it
 * (webpack.config.js `proxy`), production has to be told to; until it is, the browser
 * gets a socket that never opens, and `STREAM_ORIGIN` below is the one line to change
 * if the answer turns out to be "go cross-origin to api.sam-vision.uz instead".
 *
 * Going cross-origin is not free: the WebSocket itself needs no CORS, but
 * `POST /stream-sessions/{id}/ice-outcome` does (§4.6), which is exactly how a
 * deployment ends up with working video and a silently blind metric.
 */
import {API_BASE_URL} from '@/shared/config/env';

/**
 * Set this to an absolute origin (`https://api.sam-vision.uz`) to send signalling
 * somewhere other than the API's own origin. Empty ⇒ derive, which is what both
 * builds want today.
 */
// Annotated `string` rather than left to inference: as a bare `''` literal its type is
// `''`, and TypeScript then narrows the branch below to `never` — the check would
// stop compiling the moment it starts being useful.
const STREAM_ORIGIN: string = '';

/** The page's origin on web; empty everywhere else (React Native has no `location`). */
function pageOrigin(): string {
  const loc = (globalThis as {location?: {origin?: string}}).location;
  return loc?.origin ?? '';
}

/** The http(s) origin the stream service is reached at, without a trailing slash. */
export function streamOrigin(): string {
  if (STREAM_ORIGIN) {
    return STREAM_ORIGIN.replace(/\/+$/, '');
  }
  // Absolute base (native): strip the /api/v1 suffix back to the bare origin.
  const absolute = /^https?:\/\/[^/]+/i.exec(API_BASE_URL);
  return absolute ? absolute[0] : pageOrigin();
}

/**
 * The signalling URL. Empty only in a non-browser test environment with a relative
 * base — callers treat that as "streaming is not configured here" rather than
 * throwing, so a unit test that renders the screen does not need a fake `location`.
 */
export function streamSocketUrl(): string {
  const origin = streamOrigin();
  return origin ? `${origin.replace(/^http/i, 'ws')}/stream/ws` : '';
}
